import React, { useState, useRef, useEffect } from 'react';
import { Check, CircleAlert, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { IntegrationCanvasNode, IntegrationNodeType, NodeExecutionStatus, NodeExecutionResult } from '../types';
import { CONNECTOR_COLORS, TRIGGER_NODE_COLOR, MAPPING_NODE_COLOR, DESTINATION_NODE_COLOR } from '../constants/connector-configs';
import { getConnectorIcon, TriggerIcon, MappingIcon, DestinationIcon } from './ConnectorIcons';

interface IntegrationNodeProps {
  node: IntegrationCanvasNode;
  zoom: number;
  onDrag: (nodeId: string, deltaX: number, deltaY: number) => void;
  onDelete: (nodeId: string) => void;
  onConfigChange: (nodeId: string, config: any) => void;
  onClick?: (nodeId: string, nodeType: IntegrationNodeType) => void;
  isSelected?: boolean;
  disabled?: boolean;
  executionStatus?: NodeExecutionStatus;
  executionResult?: NodeExecutionResult;
}

export const getNodeColor = (node: IntegrationCanvasNode): string => {
  if (node.type === 'trigger') return TRIGGER_NODE_COLOR;
  if (node.type === 'source' && node.connectorType) {
    return CONNECTOR_COLORS[node.connectorType];
  }
  if (node.type === 'mapping') return MAPPING_NODE_COLOR;
  return DESTINATION_NODE_COLOR;
};

const getNodeIcon = (node: IntegrationCanvasNode) => {
  if (node.type === 'trigger') return TriggerIcon;
  if (node.type === 'source' && node.connectorType) {
    return getConnectorIcon(node.connectorType);
  }
  if (node.type === 'mapping') return MappingIcon;
  return DestinationIcon;
};

// Hexagon clip-path for trigger node
const HEXAGON_CLIP = 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)';

// Execution status badge — a coloured status dot in the node's corner.
const ExecutionBadge: React.FC<{ status: NodeExecutionStatus }> = ({ status }) => {
  if (status === 'idle') return null;

  return (
    <span
      data-testid={`execution-badge-${status}`}
      className={cn(
        'absolute -top-1.5 -right-1.5 z-10 flex size-5 items-center justify-center rounded-full text-background',
        status === 'running' && 'bg-foreground',
        status === 'completed' && 'bg-success',
        status === 'failed' && 'bg-destructive',
      )}
    >
      {status === 'running' && <Loader2 className="size-3 animate-spin" />}
      {status === 'completed' && <Check className="size-3" />}
      {status === 'failed' && <CircleAlert className="size-3" />}
    </span>
  );
};

// Border tone for the node chrome; execution state wins over selection.
const nodeBorderClass = (
  executionStatus: NodeExecutionStatus,
  isSelected: boolean,
  isActive: boolean,
): string => {
  if (executionStatus === 'completed') return 'border-success';
  if (executionStatus === 'failed') return 'border-destructive';
  if (executionStatus === 'running') return 'border-foreground';
  if (isSelected) return 'border-foreground';
  if (isActive) return 'border-foreground/40';
  return 'border-border';
};

export const IntegrationNode: React.FC<IntegrationNodeProps> = ({
  node,
  zoom,
  onDrag,
  onDelete,
  onConfigChange,
  onClick,
  isSelected = false,
  disabled = false,
  executionStatus = 'idle',
  executionResult,
}) => {
  const { t } = useTranslation();

  // Resolve node text: handles i18n keys and special "KEY:count" pattern for fields mapped
  const resolveNodeText = (text: string | undefined): string => {
    if (!text) return '';
    if (!text.startsWith('PAGES.')) return text;
    // Handle "PAGES.API_INTEGRATION.NODE_SUBTITLES.FIELDS_MAPPED:3"
    const colonIdx = text.lastIndexOf(':');
    if (colonIdx > 0 && text.includes('FIELDS_MAPPED')) {
      const key = text.substring(0, colonIdx);
      const count = text.substring(colonIdx + 1);
      return t(key, { count: parseInt(count, 10) || 0 });
    }
    return t(text);
  };

  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const dragRef = useRef<{ startX: number; startY: number; hasMoved: boolean }>({
    startX: 0, startY: 0, hasMoved: false,
  });

  const nodeColor = getNodeColor(node);
  const IconComponent = getNodeIcon(node);
  const isTrigger = node.type === 'trigger';

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragRef.current = { startX: e.clientX, startY: e.clientY, hasMoved: false };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        dragRef.current.hasMoved = true;
      }

      if (dragRef.current.hasMoved) {
        onDrag(node.id, deltaX / zoom, deltaY / zoom);
        dragRef.current.startX = e.clientX;
        dragRef.current.startY = e.clientY;
      }
    };

    const handleMouseUp = () => {
      if (!dragRef.current.hasMoved && onClick && !disabled) {
        onClick(node.id, node.type);
      }
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, node.id, node.type, onDrag, onClick, zoom]);

  // ─── Trigger node: hexagon shape ───
  if (isTrigger) {
    const hexSize = 90; // width & height of the hexagon — connection lines assume 104×90

    const hexBorderClass =
      executionStatus === 'completed' ? 'bg-success'
        : executionStatus === 'failed' ? 'bg-destructive'
        : executionStatus === 'running' ? 'bg-foreground'
        : isSelected ? 'bg-foreground'
        : (isHovered || isDragging) ? 'bg-foreground/40'
        : 'bg-border';

    return (
      <div
        data-testid={`integration-node-${node.type}`}
        className="absolute"
        style={{
          left: node.x,
          top: node.y,
          width: hexSize * 1.15,
          height: hexSize,
          zIndex: isDragging ? 1000 : (isHovered ? 100 : 1),
        }}
      >
        {/* Execution Badge */}
        <ExecutionBadge status={executionStatus} />

        {/* Hexagon border (slightly larger) */}
        <div
          className={cn('absolute z-0 transition-colors', hexBorderClass)}
          style={{
            top: -2,
            left: -2,
            width: hexSize * 1.15 + 4,
            height: hexSize + 4,
            clipPath: HEXAGON_CLIP,
          }}
        />

        {/* Hexagon body */}
        <div
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={cn(
            'relative z-[1] flex h-full w-full select-none flex-col items-center justify-center gap-0.5 bg-background transition-transform',
            isDragging ? 'scale-[1.02] cursor-grabbing' : 'cursor-pointer',
          )}
          style={{ clipPath: HEXAGON_CLIP }}
        >
          <span className="flex size-7 items-center justify-center rounded-full bg-muted text-foreground">
            <IconComponent className="size-4" />
          </span>
          <p className="text-center text-xs font-medium leading-tight text-foreground">
            {resolveNodeText(node.title)}
          </p>
          <p className="max-w-[70%] truncate text-center text-[9px] leading-tight text-muted-foreground">
            {resolveNodeText(node.subtitle)}
          </p>
        </div>

        {/* Right connection point */}
        <span className="absolute top-1/2 -right-1.5 z-[2] size-3 -translate-y-1/2 rounded-full border-2 border-border bg-muted" />
      </div>
    );
  }

  // ─── Regular nodes: source, mapping, destination — 180px wide, lines assume ~68px height ───
  return (
    <div
      data-testid={`integration-node-${node.type}`}
      className="absolute w-[180px]"
      style={{
        left: node.x,
        top: node.y,
        zIndex: isDragging ? 1000 : (isHovered ? 100 : 1),
      }}
    >
      {/* Execution Badge */}
      <ExecutionBadge status={executionStatus} />

      <div
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'relative w-full select-none rounded-xl border bg-background p-3 transition-all',
          nodeBorderClass(executionStatus, isSelected, isHovered || isDragging),
          // No resting shadow: the ground is dotted and the separation comes
          // from the hairline border, as everywhere else. The lift stays on
          // drag, where it is telling you the node has left the canvas.
          disabled ? 'cursor-not-allowed opacity-50' : (isDragging ? 'scale-[1.02] cursor-grabbing shadow-md' : 'cursor-pointer'),
          executionStatus === 'running' && 'ring-2 ring-foreground/10',
        )}
      >
        {/* Delete Button — source only */}
        {node.type === 'source' && (
          <div
            className={cn(
              'absolute top-1.5 right-1.5 flex gap-1 transition-opacity',
              isHovered ? 'opacity-100' : 'opacity-0',
            )}
          >
            {/* Local provider: nodes are rendered outside the app shell in tests */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon-xs"
                    className="hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(node.id);
                    }}
                    aria-label={t('PAGES.API_INTEGRATION.DELETE')}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('PAGES.API_INTEGRATION.DELETE')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
            {/* Source nodes carry a brand mark in its brand colour; the rest are monochrome. */}
            <IconComponent
              className="size-4"
              style={node.type === 'source' ? { color: nodeColor } : undefined}
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {resolveNodeText(node.title)}
            </p>
          </div>
        </div>

        {/* Subtitle */}
        <p className="mt-1 text-xs leading-snug text-muted-foreground">
          {resolveNodeText(node.subtitle)}
        </p>

        {/* Execution result summary badge */}
        {executionResult && (executionStatus === 'completed' || executionStatus === 'failed') && (
          <span
            data-testid="execution-result-badge"
            className={cn(
              'mt-1 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5',
              executionStatus === 'completed'
                ? 'border-success/30 bg-success/10 text-success'
                : 'border-destructive/30 bg-destructive/10 text-destructive',
            )}
          >
            {executionStatus === 'completed' ? (
              <Check className="size-2.5" />
            ) : (
              <CircleAlert className="size-2.5" />
            )}
            <span className="text-[10px] font-medium leading-none">
              {executionResult.summary}
            </span>
          </span>
        )}

        {/* Connection Points */}
        <span className="absolute top-1/2 -left-1.5 z-[2] size-3 -translate-y-1/2 rounded-full border-2 border-border bg-muted" />
        {node.type !== 'destination' && (
          <span className="absolute top-1/2 -right-1.5 z-[2] size-3 -translate-y-1/2 rounded-full border-2 border-border bg-muted" />
        )}
      </div>
    </div>
  );
};
